// v2/features/residence-verification/services/rv-service.ts — Residence verification API calls

import { apiGet, apiPost } from '../../../shared/api/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RvAddress {
  type?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  postalCode?: number;
  ownership?: string;
  monthlyRent?: number;
  yearsOfStay?: number;
  active?: boolean;
  verified?: boolean;
}

export interface RvAdditionalResidentialDetails {
  spouseName?: string;
  spouseAwareOfLoan?: boolean;
  livingStandard?: string;
  accessibility?: string;
  electricityBillNo?: string;
  electricityBillAmount?: number;
  overdueElectricityBillAmount?: number;
  gatedHouse?: boolean;
  kitchen?: boolean;
  communityDominatedArea?: boolean;
  earningMemberCount?: number;
  occupantsCount?: number;
  childrenStudyingCount?: number;
  roomsCount?: number;
  dependentMemberCount?: number;
  monthlyResidentialExpenses?: number;
  appliedLoanAmount?: number;
  applicantStayStatus?: string;
  addressIsSameAsKYC?: boolean;
}

export interface RvPayload {
  address?: RvAddress;
  additionalResidentialDetails?: RvAdditionalResidentialDetails;
  stayStatus?: string;
  thirdPartyReference?: boolean;
  remarks?: string;
  documents?: string[];
}

export type ResidenceVerificationAggregateStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'COMPLETED';

export interface ResidenceVerificationAggregate {
  applicationId: string;
  status: ResidenceVerificationAggregateStatus;
  version: number;
  address?: RvAddress;
  additionalResidentialDetails?: RvAdditionalResidentialDetails;
  stayStatus?: string;
  thirdPartyReference?: boolean;
  remarks?: string;
  documents?: string[];
  completedAt?: string;
  completedBy?: string;
  submitterRole?: string;
  lastUpdatedAt?: string;
}

export interface ResidenceVerificationResponse {
  applicationId: string;
  verificationStatus: string;
  loanState?: string;
  version?: number;
  aggregate?: ResidenceVerificationAggregate;
}

export type ResidenceVerificationErrorCode =
  | 'ADDRESS_PREREQUISITE_NOT_MET'
  | 'RESIDENCE_VERIFICATION_INCOMPLETE'
  | 'RESIDENCE_VERIFICATION_POLICY_DENIED'
  | 'RESIDENCE_VERIFICATION_PRODUCT_NOT_ENABLED'
  | 'RESIDENCE_VERIFICATION_ALREADY_COMPLETED'
  | 'RESIDENCE_VERIFICATION_VERSION_CONFLICT';

/** @deprecated Kept for backward compatibility with the old simple flow. */
export interface ResidenceVerificationData {
  applicationId: string;
  addressLineOne: string;
  addressLineTwo?: string;
  city: string;
  state: string;
  postalCode: string;
  verificationStatus: string;
  remarks?: string;
  verifiedAt?: string;
  verifiedBy?: string;
}

// ─── API Paths ───────────────────────────────────────────────────────────────

const API_PATHS = {
  VERIFICATION: '/residence-verification',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Generate a unique idempotency key for residence verification requests.
 * Used to prevent duplicate transitions on retry.
 */
export function generateIdempotencyKey(): string {
  return `rv-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Extract a typed error code from an API error response.
 * Supports both pre-parsed error objects (`{ code }`) and raw axios
 * errors (`{ response: { data: { code } } }`).
 */
export function extractRvErrorCode(
  error: unknown,
): ResidenceVerificationErrorCode | undefined {
  if (error == null) return undefined;
  if (typeof error !== 'object') return undefined;

  // Pre-parsed API error shape: { status, message, code }
  if ('code' in error && typeof (error as Record<string, unknown>).code === 'string') {
    return (error as Record<string, unknown>).code as ResidenceVerificationErrorCode;
  }

  // Axios-style error: { response: { data: { code } } }
  const response = (error as Record<string, unknown>).response;
  if (response && typeof response === 'object') {
    const data = (response as Record<string, unknown>).data;
    if (data && typeof data === 'object' && 'code' in data) {
      return (data as Record<string, unknown>).code as ResidenceVerificationErrorCode;
    }
  }

  return undefined;
}

// ─── API Functions ───────────────────────────────────────────────────────────

/**
 * Fetch the current residence verification aggregate for an application.
 * Returns `null` when no verification exists yet (404).
 * Re-throws all other errors.
 */
export async function getResidenceVerification(
  applicationId: string,
  config?: { signal?: AbortSignal },
): Promise<ResidenceVerificationResponse | null> {
  try {
    const encoded = encodeURIComponent(applicationId);
    const response = await apiGet<ResidenceVerificationResponse>(
      `${API_PATHS.VERIFICATION}/${encoded}`,
      config?.signal ? { signal: config.signal } : undefined,
    );
    return response.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'status' in error && (error as Record<string, unknown>).status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Save a draft of the residence verification (partial data allowed).
 * Optionally accepts an idempotency key.
 */
export async function draftResidenceVerification(
  applicationId: string,
  payload: RvPayload,
  options?: { idempotencyKey?: string },
): Promise<ResidenceVerificationResponse> {
  const encoded = encodeURIComponent(applicationId);
  const headers: Record<string, string> = {};
  if (options?.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  const response = await apiPost<ResidenceVerificationResponse>(
    `${API_PATHS.VERIFICATION}/${encoded}/draft`,
    payload,
    { headers },
  );
  return response.data;
}

/**
 * Complete the residence verification (final submission).
 * Requires an idempotency key to guard against duplicate transitions.
 */
export async function completeResidenceVerification(
  applicationId: string,
  payload: RvPayload,
  options: { idempotencyKey: string },
): Promise<ResidenceVerificationResponse> {
  if (!options?.idempotencyKey) {
    throw new Error(
      'Idempotency-Key is required for completeResidenceVerification to prevent duplicate transitions.',
    );
  }
  const encoded = encodeURIComponent(applicationId);
  const response = await apiPost<ResidenceVerificationResponse>(
    `${API_PATHS.VERIFICATION}/${encoded}/complete`,
    payload,
    { headers: { 'Idempotency-Key': options.idempotencyKey } },
  );
  return response.data;
}

// ─── Legacy Functions (backward compat) ──────────────────────────────────────

/** @deprecated Use `getResidenceVerification` instead. */
export async function getVerification(
  applicationId: string,
  config?: { signal?: AbortSignal },
): Promise<ResidenceVerificationData | null> {
  try {
    const response = await apiGet<ResidenceVerificationData>(
      `${API_PATHS.VERIFICATION}/${applicationId}`,
      config?.signal ? { signal: config.signal } : undefined,
    );
    return response.data;
  } catch {
    // Return null if no verification exists yet
    return null;
  }
}

/** @deprecated Use `draftResidenceVerification` / `completeResidenceVerification` instead. */
export async function submitVerification(
  applicationId: string,
  data: Omit<ResidenceVerificationData, 'applicationId'>,
): Promise<ResidenceVerificationData> {
  const response = await apiPost<ResidenceVerificationData>(
    `${API_PATHS.VERIFICATION}/${applicationId}`,
    data,
  );
  return response.data;
}
